import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextVariant,
} from "@/mds";
import {
  AGENT_SKILL_FIELD_LIMITS,
  type AgentSkill,
  type CreateAgentSkill,
} from "@/pages/agent/skills/agent-skill.types";

export const AgentSkillEditor = ({
  skill,
  prefill = null,
  banner = null,
  header,
  submitLabel,
  isOpen,
  isSaving,
  error,
  onClose,
  onSave,
}: {
  skill: AgentSkill | null;
  prefill?: CreateAgentSkill | null;
  banner?: string | null;
  header?: string;
  submitLabel?: string;
  isOpen: boolean;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (value: CreateAgentSkill) => void;
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [name, setName] = useState("");
  const [whenToUse, setWhenToUse] = useState("");
  const [instructions, setInstructions] = useState("");

  useEffect(() => {
    setName(skill?.name ?? prefill?.name ?? "");
    setWhenToUse(skill?.whenToUse ?? prefill?.when_to_use ?? "");
    setInstructions(skill?.instructions ?? prefill?.instructions ?? "");
  }, [skill, prefill, isOpen]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSave({ name: name.trim(), when_to_use: whenToUse.trim(), instructions: instructions.trim() });
  };

  return (
    <MDSModalDialog isOpen={isOpen} handleClose={onClose}>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        {header ?? (skill ? "Edit skill" : "Create skill")}
      </MDSText>
      <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
        {banner ? <StyledBanner>{banner}</StyledBanner> : null}
        <StyledForm ref={formRef} onSubmit={submit}>
          <StyledLabel>
            Name
            <MDSTextField
              aria-label="Name"
              autoFocus
              maxLength={AGENT_SKILL_FIELD_LIMITS.name}
              value={name}
              variant={MDSTextFieldVariant.Form}
              onChange={setName}
            />
          </StyledLabel>
          <StyledLabel>
            When to use
            <MDSTextField
              aria-label="When to use"
              isTextArea
              maxLength={AGENT_SKILL_FIELD_LIMITS.whenToUse}
              textAreaProps={{ rows: 3 }}
              value={whenToUse}
              variant={MDSTextFieldVariant.Form}
              onChange={setWhenToUse}
            />
          </StyledLabel>
          <StyledLabel>
            Instructions
            <MDSTextField
              aria-label="Instructions"
              isTextArea
              maxLength={AGENT_SKILL_FIELD_LIMITS.instructions}
              textAreaProps={{ rows: 9 }}
              value={instructions}
              variant={MDSTextFieldVariant.Form}
              onChange={setInstructions}
            />
          </StyledLabel>
          {error ? <StyledError role="alert">{error}</StyledError> : null}
        </StyledForm>
      </MDSDialogTextContent>
      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton variant={MDSButtonVariant.Transparent} onPress={onClose}>
          Cancel
        </MDSButton>
        <MDSButton
          isDisabled={isSaving || !name.trim() || !whenToUse.trim() || !instructions.trim()}
          variant={MDSButtonVariant.Contrast}
          onPress={() => formRef.current?.requestSubmit()}
        >
          {isSaving ? "Saving…" : (submitLabel ?? (skill ? "Save" : "Create"))}
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const StyledLabel = styled.label`
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledError = styled.div`
  color: ${({ theme }) => theme.colors.text.danger};
`;

const StyledBanner = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;
